import { $, html, Html, View } from 'rune-ts';
import Sortable from 'sortablejs';
import klass from './ProductThumbnailForm.module.scss';
import { CreatorProductData } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { font } from '@services/MarppleShop/shared/typography';
import { ButtonAction } from '@services/MarppleShop/shared/components/atoms/ButtonAction/ButtonAction.ts';
import { classes } from '@services/MarppleShop/shared/util';
import { Alert } from '@services/MarppleShop/shared/components/cells/Alert/Alert.ts';
import {
  FileWithUrl,
  InvalidFileInfo,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductThumbnailForm/types.ts';
import {
  createAlertMessage,
  validateImages,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductThumbnailForm/helper.ts';
import { noop } from '@fxts/core';
import { ButtonRadio } from '@services/MarppleShop/renderApp/components/atoms/ButtonRadio/ButtonRadio.ts';
import {
  IMAGE_TYPES_ACCEPT,
  MAX_THUMBNAIL_COUNT,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductThumbnailForm/constant.ts';

export class ProductThumbnailForm extends View<CreatorProductData> {
  private filesList: FileWithUrl[] = [];
  private currentDisplayedImageIdx = -1; // 현재 보여지는 이미지 인덱스 (-1은 아무것도 없는 상태)
  private sortableInstance: Sortable | null = null; // 추가

  private buttonView = new ButtonAction({
    label: '이미지 등록하기',
    type: 'blue',
    size: 'small',
  });

  private applyMainImageButtonView = new ButtonRadio({
    active: this.currentDisplayedImageIdx === 1,
    label: '대표 이미지로 지정',
  });

  protected override template(): Html {
    return html`
      <div class="${klass.root}">
        <section class="${klass.title_section} ${font('16_bold')}">상품 대표 이미지</section>

        <!-- 이미지 업로드/표시 영역 -->
        <section class="${klass.upload_display_section}">
          <!-- 썸네일 슬롯 영역 - 이미지 등록 전에는 표시 안 함 -->
          <div
            class="${classes(klass.thumbnails_wrap, {
              [klass.hidden]: !this.filesList.length,
            })} "
          >
            <div class="${klass.thumbnail_list}">
              ${this.filesList.length > 0 ? this.makeThumbnailSlotsHtml() : ''}
            </div>
          </div>
          ${this.makeDisplayImageHtml()} ${this.makeUploadAreaHtml()}
        </section>

        <section class="${klass.etc_section}">
          <div
            class="${classes(klass.apply_main_image_button_wrap, {
              [klass.hidden]: !this.filesList.length,
            })}"
          >
            ${this.applyMainImageButtonView}
          </div>
          <div class="${klass.image_upload_info_box}">
            <div class="${klass.image_upload_info} ${font('12_medium')}">
              이미지 권장 사이즈 : 정사각형 비율의 740 ~ 1480px
              <span class="${klass.divide}"></span>
              등록 가능 형식 : JPG, PNG
              <span class="${klass.divide}"></span>
              용량 5MB 이하
            </div>
          </div>
        </section>
      </div>
    `;
  }

  override onRender() {
    // 버튼 클릭 이벤트 설정
    this.delegate('click', ButtonAction, () => {
      this.element().querySelector<HTMLElement>(`.${klass.file_input}`)?.click();
    });

    // 대표 이미지 선택
    this.addEventListener(ButtonRadio.ClickEvent, () => {
      this.setCurrentImageAsMain();
    });

    // 파일 입력 변경 이벤트 설정
    this.delegate('change', `.${klass.file_input}`, (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        this.handleFiles(target.files).then(noop);
      }
    });

    this.setupDropEvents();
    this.setupThumbnailEvents();
    this.initSortable();
  }

  private setCurrentImageAsMain() {
    const currentIdx = this.currentDisplayedImageIdx;
    if (currentIdx <= 0 || this.filesList.length <= 1) {
      return;
    }

    const currentImage = this.filesList[currentIdx];
    if (!currentImage) {
      return;
    }

    const otherImages = this.filesList.filter((_, index) => index !== currentIdx);

    this.filesList = [currentImage, ...otherImages];
    this.currentDisplayedImageIdx = 0;
    this.applyMainImageButtonView.setActive(true);
    this.redraw();

    requestAnimationFrame(() => {
      this.initSortable();
    });
  }

  private makeThumbnailSlotsHtml(): Html {
    return html`
      ${this.filesList.map((fileData, index) => {
        return html`
          <div
            class="${classes(klass.thumbnail_item, {
              [klass.selected]: this.currentDisplayedImageIdx === index,
            })}"
            data-thumbnail-index="${index}"
          >
            <div class="${klass.thumbnail_content}">
              <div class="${klass.thumbnail_image_container}">
                <img src="${fileData.url}" alt="썸네일 이미지" class="${klass.thumbnail_image}" />
              </div>
              <button type="button" class="${klass.delete_button}" data-remove-index="${index}">
                <img
                  src="//s3.marpple.co/files/u_2308668/2025/5/original/7cb23d7333b1fe597e001cccc30e98b659c604f51.svg"
                  alt="trash icon"
                />
              </button>
            </div>
          </div>
        `;
      })}
      <!-- 새 이미지 추가를 위한 + 버튼 -->
      ${this.filesList.length < MAX_THUMBNAIL_COUNT
        ? html`
            <div class="${klass.thumbnail_item} ${klass.empty_slot}">
              <div class="${klass.thumbnail_content}">
                <div class="${klass.plus_icon}">
                  <img
                    src="//s3.marpple.co/files/u_2308668/2025/5/original/d3b920c423c695e91b64b43871e6388cc5553fd11.svg"
                    alt="+ shape icon"
                  />
                </div>
              </div>
            </div>
          `
        : html``}
    `;
  }

  private makeDisplayImageHtml(): Html {
    const fileData = this.filesList[this.currentDisplayedImageIdx] || {
      url: '',
      file: {
        name: '',
      },
    };

    return html`
      <div
        class="${classes(klass.display_image_container, {
          [klass.hidden]: this.currentDisplayedImageIdx === -1,
        })}"
      >
        <div class="${klass.display_image_content}">
          <img src="${fileData.url}" alt="${fileData.file.name}" class="${klass.detailed_image}" />
        </div>
      </div>
    `;
  }

  private makeUploadAreaHtml(): Html {
    return html`
      <div
        class="${classes(klass.upload_area, {
          [klass.hidden]: this.currentDisplayedImageIdx > -1,
        })}"
      >
        <div class="${klass.upload_content}">
          <div class="${klass.icon_wrap}">
            <img
              src="//s3.marpple.co/files/u_2308668/2025/5/original/e7a42661d7ade3d7260886e56179def57951ffea1.svg"
              alt="상품 등록 안내 이미지"
            />
          </div>
          <div class="${klass.text_info_wrap}">
            <div class="${font('16_bold')}">상품 이미지를 등록해 주세요.</div>
            <div class="${font('14_medium')}">구매자의 눈길을 사로잡을 이미지를 등록해 보세요!</div>
          </div>
          <div class="${klass.button_container}">
            <input
              type="file"
              id="product-image-upload"
              class="${klass.file_input}"
              accept="${IMAGE_TYPES_ACCEPT}"
              multiple
            />
            <div>${this.buttonView}</div>
          </div>
        </div>
      </div>
    `;
  }

  private setupDropEvents() {
    const upload_area_selector = `.${klass.upload_area},.${klass.display_image_container}`;

    this.delegate('dragover', upload_area_selector, (e) => {
      e.originalEvent.preventDefault();
      e.originalEvent.stopPropagation();
      (e.currentTarget as HTMLElement).classList.add(klass.dragover);
    })
      .delegate('dragleave', upload_area_selector, (e) => {
        e.originalEvent.preventDefault();
        e.originalEvent.stopPropagation();
        (e.currentTarget as HTMLElement).classList.remove(klass.dragover);
      })
      .delegate('drop', upload_area_selector, (e) => {
        e.originalEvent.preventDefault();
        e.originalEvent.stopPropagation();
        (e.currentTarget as HTMLElement).classList.remove(klass.dragover);

        const dragEvent = e as DragEvent;
        if (dragEvent.dataTransfer?.files.length) {
          this.handleFiles(dragEvent.dataTransfer.files).then(noop);
        }
      });
  }

  private setupThumbnailEvents() {
    this.delegate('click', `.${klass.thumbnail_item}:not(.${klass.empty_slot})`, (e) => {
      const thumbnail_item_el = e.currentTarget as HTMLElement;
      if (
        thumbnail_item_el.classList.contains(klass.selected) ||
        (e.target as HTMLElement).closest(`.${klass.delete_button}`)
      ) {
        return;
      }

      this.element()
        .querySelectorAll(`.${klass.thumbnail_item}.${klass.selected}`)
        .forEach((el) => {
          el.classList.remove(klass.selected);
        });

      thumbnail_item_el.classList.add(klass.selected);

      const index = parseInt(thumbnail_item_el.getAttribute('data-thumbnail-index') || '-1');
      if (index >= 0 && index < this.filesList.length) {
        this.applyMainImageButtonView.setActive(index === 0);
        this.showDetailedImage(index);
      }
    });

    this.delegate('click', `.${klass.delete_button}`, (e) => {
      e.originalEvent.stopPropagation();

      const delete_button_el = e.currentTarget as HTMLElement;
      const index = parseInt(delete_button_el.getAttribute('data-remove-index') || '-1');

      if (index >= 0 && index < this.filesList.length) {
        this.removeFile(index);
      }
    });

    this.delegate('click', `.${klass.empty_slot}`, () => {
      if (this.currentDisplayedImageIdx === -1) return;
      this.applyMainImageButtonView.setActive(false);
      this.resetToUploadState();
    });
  }

  private async handleFiles(selectedFiles: FileList) {
    const newFilesArray = Array.from(selectedFiles);

    // 기본 유효성 검사
    if (newFilesArray.length === 0) {
      this.resetFileInput();
      return;
    }

    // 이미지 개수 검사
    const currentFileCount = this.filesList.length;
    if (currentFileCount >= MAX_THUMBNAIL_COUNT) {
      await Alert.open({
        title: '업로드 공간 부족',
        message: `최대 ${MAX_THUMBNAIL_COUNT}개의 이미지만 등록 가능합니다. 현재 ${currentFileCount}개가 선택되어 더 이상 추가할 수 없습니다.`,
      });
      this.resetFileInput();
      return;
    }

    // 남은 슬롯 검사
    const remainingSlots = MAX_THUMBNAIL_COUNT - currentFileCount;
    if (newFilesArray.length > remainingSlots) {
      await Alert.open({
        title: '선택 이미지 수 초과',
        message: `현재 추가할 수 있는 이미지는 최대 ${remainingSlots}개입니다.\n현재 ${newFilesArray.length}개를 선택하셨습니다.\n이미지 수를 줄여 다시 시도해주세요.`,
      });
      this.resetFileInput();
      return;
    }

    // 이미지 파일 유효성 검사
    const invalidFiles: InvalidFileInfo[] = [];
    const validFiles = await validateImages(newFilesArray, invalidFiles);

    if (invalidFiles.length > 0) {
      const alertOptions = createAlertMessage(invalidFiles, validFiles.length);
      await Alert.open(alertOptions);
    }

    if (validFiles.length === 0) {
      this.resetFileInput();
      return;
    }

    // 유효한 이미지 처리
    const filesWithUrls = validFiles.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));

    // 파일 목록에 새 이미지 추가
    this.filesList = [...this.filesList, ...filesWithUrls];

    // 항상 첫 번째 이미지를 대표 이미지로 설정하고 표시
    this.currentDisplayedImageIdx = 0;
    this.applyMainImageButtonView.setActive(true);

    this.resetFileInput();
    this.redraw();

    // 썸네일 selected 상태는 redraw 후에 설정해야 함
    requestAnimationFrame(() => {
      const thumbnailItems = this.element().querySelectorAll(`.${klass.thumbnail_item}`);
      thumbnailItems.forEach((item, index) => {
        if (index === 0) {
          item.classList.add(klass.selected);
        } else {
          item.classList.remove(klass.selected);
        }
      });

      this.initSortable();
    });
  }

  private showDetailedImage(index: number) {
    if (index >= 0 && index < this.filesList.length) {
      this.currentDisplayedImageIdx = index;
      this.element().querySelector(`.${klass.upload_area}`)?.classList.add(klass.hidden);
      $(this.element())
        .find(`.${klass.display_image_container}`)
        ?.removeClass(klass.hidden)
        .find('img')
        ?.chain((img_el) => {
          const file_data = this.filesList[index];
          if (file_data) {
            (img_el as HTMLImageElement).src = file_data.url;
            (img_el as HTMLImageElement).alt = file_data.file.name;
          }
        });
    }
  }

  private resetToUploadState() {
    this.currentDisplayedImageIdx = -1;
    this.element()
      .querySelectorAll(`.${klass.thumbnail_item}.${klass.selected}`)
      .forEach((el) => {
        el.classList.remove(klass.selected);
      });
    this.element().querySelector(`.${klass.upload_area}`)?.classList.remove(klass.hidden);
    this.element().querySelector(`.${klass.display_image_container}`)?.classList.add(klass.hidden);
  }

  private removeFile(index: number) {
    if (index >= 0 && index < this.filesList.length) {
      // Object URL 해제
      const url = this.filesList[index]?.url;
      url && URL.revokeObjectURL(url);

      // 현재 표시 중인 이미지인 경우 업로드 화면으로 돌아감
      if (this.currentDisplayedImageIdx === index) {
        this.currentDisplayedImageIdx = -1;
      } else if (this.currentDisplayedImageIdx > index) {
        // 삭제된 이미지 이후의 이미지를 보고 있었다면 인덱스 조정
        this.currentDisplayedImageIdx--;
      }

      // 파일 목록에서 제거
      this.filesList.splice(index, 1);

      this.redraw();

      requestAnimationFrame(() => {
        this.initSortable();
      });
    }
  }

  private resetFileInput(): void {
    const file_input_el = this.element().querySelector<HTMLInputElement>(`.${klass.file_input}`);
    if (file_input_el) {
      file_input_el.value = '';
    }
  }

  private initSortable(): void {
    if (this.filesList.length <= 1 && this.sortableInstance) {
      if (this.sortableInstance) {
        this.sortableInstance.destroy();
        this.sortableInstance = null;
      }
      return;
    }

    const thumbnailList = this.element().querySelector<HTMLElement>(`.${klass.thumbnail_list}`);
    if (!thumbnailList) return;

    if (this.sortableInstance) {
      this.sortableInstance.destroy();
    }

    const sortable_item_klass = 'sortable-item';
    // 이미지와 빈 슬롯을 분리하기 위한 클래스 추가
    thumbnailList.querySelectorAll(`.${klass.thumbnail_item}:not(.${klass.empty_slot})`).forEach((item) => {
      item.classList.add(sortable_item_klass);
    });

    // 새 Sortable 인스턴스 생성
    this.sortableInstance = new Sortable(thumbnailList, {
      animation: 150,
      chosenClass: klass.sortable_chosen,
      ghostClass: klass.sortable_ghost,
      filter: `.${klass.empty_slot}`,
      preventOnFilter: true,
      handle: `.${klass.thumbnail_content}`,
      draggable: `.${sortable_item_klass}`,
      onStart: function () {
        thumbnailList.classList.add(klass.dragging_active);
      },
      onEnd: (evt) => {
        thumbnailList.classList.remove(klass.dragging_active);

        if (evt.oldIndex !== undefined && evt.newIndex !== undefined && evt.oldIndex !== evt.newIndex) {
          this.reorderFiles(evt.oldIndex, evt.newIndex);
        }
      },
    });
  }

  private reorderFiles(oldIndex: number, newIndex: number): void {
    if (oldIndex === newIndex) return;

    const currentSelectedIdx = this.currentDisplayedImageIdx;

    // 배열 요소 재정렬
    const item = this.filesList.splice(oldIndex, 1)[0];
    item && this.filesList.splice(newIndex, 0, item);

    // 현재 선택한 이미지 인덱스 조정
    if (currentSelectedIdx === oldIndex) {
      // 드래그한 아이템이 현재 선택된 아이템인 경우
      this.currentDisplayedImageIdx = newIndex;
    } else if (currentSelectedIdx > oldIndex && currentSelectedIdx <= newIndex) {
      // 이동한 아이템이 현재 선택된 아이템보다 앞에 있었고, 뒤로 이동한 경우
      this.currentDisplayedImageIdx--;
    } else if (currentSelectedIdx < oldIndex && currentSelectedIdx >= newIndex) {
      // 이동한 아이템이 현재 선택된 아이템보다 뒤에 있었고, 앞으로 이동한 경우
      this.currentDisplayedImageIdx++;
    }

    // 대표 이미지 설정 여부 확인
    this.applyMainImageButtonView.setActive(this.currentDisplayedImageIdx === 0);

    this.redraw();

    // 썸네일 selected 상태 업데이트
    requestAnimationFrame(() => {
      const thumbnailItems = this.element().querySelectorAll(`.${klass.thumbnail_item}`);
      thumbnailItems.forEach((item, index) => {
        item.classList.toggle(klass.selected, index === this.currentDisplayedImageIdx);
      });
    });

    this.initSortable();
  }

  getFiles(): File[] {
    return this.filesList.map((fileData) => fileData.file);
  }
}
